<script setup lang="ts">
	import { PencilSquareIcon } from '@heroicons/vue/20/solid'
	import { computed, ref, watch } from 'vue'
	import { useRoute } from 'vue-router'

	import BreadCrumb, { type Page } from '@/components/BreadCrumb.vue'
	import HasPermission from '@/components/HasPermission.vue'
	import { Skeleton } from '@/components/ui/skeleton'
	import {
		Tabs,
		TabsContent,
		TabsList,
		TabsTrigger,
	} from '@/components/ui/tabs'

	import { useAuth } from '@/composables/useAuth'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import useUser from '@/modules/users/composables/useUser'

	import FeedbackTab from './partials/show/FeedbackTab.vue'
	import GeneralTab from './partials/show/GeneralTab.vue'
	import LogTab from './partials/show/LogTab.vue'

	const { user, isLoading } = useUser({})
	const { user: userLogged } = useAuth()
	const { hasPermissionUser } = useHasPermissionUser()

	const pages = computed<Page[]>(() => [
		{ name: 'Usuarios', to: { name: 'users.index' } },
		{
			name: user.value?.fullName ?? '',
			to: { name: 'users.show', params: { id: user.value?.id } },
			current: true,
		},
	])

	const route = useRoute()

	/**
	 * Pestaña activa, leída de la URL.
	 *
	 * Era `default-value="general"` fijo, y eso rompía el deep link de la
	 * campana: la notificación lleva `?feedback=123`, pero quien lo lee es
	 * `FeedbackTab`, y con la pestaña general activa ese componente ni siquiera
	 * se monta — su `onMounted` nunca corre y la tarjeta nunca se expande. El
	 * enlace llegaba a la página correcta y no pasaba nada.
	 *
	 * Se valida contra la lista de pestañas conocidas en vez de confiar en el
	 * query: `?tab=cualquier-cosa` tiene que caer en «general», no dejar la
	 * pantalla sin ninguna activa.
	 */
	const TABS = ['general', 'feedback', 'log'] as const

	/**
	 * Desde ENL-54 la pestaña puede venir de dos lados, y los dos siguen vivos:
	 *
	 *  - la ruta con segmentos, `/users/838/feedback/29`, que es la que arma la
	 *    campana hoy y donde la pestaña la dice el NOMBRE de la ruta;
	 *  - el query viejo, `?tab=feedback&feedback=29`, que sigue leyéndose porque
	 *    hay campanas ya emitidas en la base con ese formato y una notificación
	 *    no se puede reescribir después de mandada.
	 */
	const tabFromRoute = (): string => {
		if (route.name === 'users.show.feedback') {
			return 'feedback'
		}

		return TABS.includes(route.query.tab as (typeof TABS)[number]) ?
				String(route.query.tab)
			:	'general'
	}

	const activeTab = ref<string>(tabFromRoute())

	// La campana puede navegar a este mismo perfil con otra pestaña sin
	// desmontar la página; sin el watch, el segundo clic no haría nada.
	watch(
		() => [route.name, route.query.tab],
		() => {
			activeTab.value = tabFromRoute()
		}
	)

	const showLogTab = computed(() => userLogged.value?.internal === true)

	// Solo en el perfil propio: el endpoint `/api/feedback` está scopeado al dueño
	// del token, así que en el perfil de otro usuario mostraría el feedback del que
	// está viendo la página, no el del titular del perfil.
	const showFeedbackTab = computed(
		() => !!user.value && userLogged.value?.id === user.value.id
	)

	const canEdit = computed(
		() =>
			user.value &&
			(userLogged.value?.id === user.value.id ||
				(hasPermissionUser('update-users', 'App\\Models\\Company') &&
					user.value.isOwned))
	)
</script>

<template>
	<BreadCrumb :pages="pages" />

	<div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
		<!-- Profile header card -->
		<div
			class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
		>
			<div
				class="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5"
			>
				<!-- Avatar skeleton / real -->
				<div class="flex-shrink-0">
					<Skeleton
						v-if="isLoading"
						class="h-20 w-20 rounded-full"
					/>
					<img
						v-else
						class="h-20 w-20 rounded-full object-cover ring-4 ring-gray-100"
						:src="
							user?.avatar ||
							`https://ui-avatars.com/api/?name=${user?.name}&background=021F58&color=fff&size=128`
						"
						alt="Foto de perfil"
					/>
				</div>

				<!-- Info skeleton / real -->
				<div class="flex-1 min-w-0 space-y-2">
					<template v-if="isLoading">
						<Skeleton class="h-6 w-48 rounded" />
						<Skeleton class="h-4 w-36 rounded" />
						<Skeleton class="h-3 w-24 rounded" />
					</template>
					<template v-else>
						<h1 class="text-xl font-bold text-gray-900 leading-tight">
							{{ user?.name }} {{ user?.secondName }} {{ user?.lastname }}
							{{ user?.secondLastname }}
						</h1>
						<p
							v-if="user?.email"
							class="text-sm text-gray-500"
						>
							{{ user.email }}
						</p>
						<p
							v-if="user?.companyName"
							class="text-xs text-gray-400"
						>
							{{ user.companyName }}
						</p>
					</template>
				</div>

				<!-- Edit button -->
				<RouterLink
					v-if="canEdit"
					:to="{ name: 'users.edit', params: { id: user?.id } }"
					class="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#253C70] text-white text-sm font-medium hover:bg-[#1e3260] transition-colors flex-shrink-0"
				>
					<PencilSquareIcon class="h-4 w-4" />
					Editar
				</RouterLink>
			</div>
		</div>

		<!-- Tabs skeleton / real -->
		<div
			class="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden"
		>
			<!-- Skeleton tab content -->
			<template v-if="isLoading">
				<div class="border-b border-gray-100 px-4 py-3 flex gap-6">
					<Skeleton class="h-4 w-32 rounded" />
				</div>
				<div class="p-6 space-y-5">
					<div class="grid grid-cols-1 sm:grid-cols-2 gap-6">
						<div
							v-for="n in 4"
							:key="n"
							class="space-y-2"
						>
							<Skeleton class="h-3 w-20 rounded" />
							<Skeleton class="h-4 w-40 rounded" />
						</div>
					</div>
				</div>
			</template>

			<Tabs
				v-else
				v-model="activeTab"
			>
				<div class="border-b border-gray-100 px-4">
					<TabsList class="bg-transparent h-auto p-0 gap-0 rounded-none">
						<TabsTrigger
							value="general"
							class="rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-medium text-gray-500 data-[state=active]:border-[#253C70] data-[state=active]:text-[#253C70] data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-gray-700 transition-colors"
						>
							Información general
						</TabsTrigger>
						<TabsTrigger
							v-if="showFeedbackTab"
							value="feedback"
							class="rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-medium text-gray-500 data-[state=active]:border-[#253C70] data-[state=active]:text-[#253C70] data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-gray-700 transition-colors"
						>
							Mis reportes
						</TabsTrigger>
						<TabsTrigger
							v-if="showLogTab"
							value="log"
							class="rounded-none border-b-2 border-transparent px-4 py-3 text-sm font-medium text-gray-500 data-[state=active]:border-[#253C70] data-[state=active]:text-[#253C70] data-[state=active]:bg-transparent data-[state=active]:shadow-none hover:text-gray-700 transition-colors"
						>
							Registro de cambios
						</TabsTrigger>
					</TabsList>
				</div>

				<TabsContent
					value="general"
					class="mt-0 p-0"
				>
					<GeneralTab
						v-if="user"
						:user="user"
					/>
				</TabsContent>

				<TabsContent
					v-if="showFeedbackTab"
					value="feedback"
					class="mt-0 p-0"
				>
					<FeedbackTab />
				</TabsContent>

				<TabsContent
					v-if="showLogTab"
					value="log"
					class="mt-0 p-0"
				>
					<LogTab
						v-if="user"
						:user="user"
					/>
				</TabsContent>
			</Tabs>
		</div>
	</div>
</template>
